import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  EntitlementActorType,
  EntitlementGrantType,
} from '@/types/entitlement-grant.types';
import { useCreateEntitlementGrantMutation } from '@/services/EntitlementGrantService';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import Select from 'react-select';
import { useGetUsersQuery } from '@/services/UserManagementService';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { useGetYachtsQuery } from '@/services/YatchManagementService';
import { useGetRegisteredDMOsQuery } from '@/services/DMOManagementService';
import { useGetServiceProvidersQuery } from '@/services/ServiceProviderService';
import { UserTypes, SubscriptionType } from '@/constant';
import { UserResponse } from '@/types/user-management.types';
import { HarborResponse } from '@/types/harbor-management.types';
import { IYachtData } from '@/types/yacht-management.types';
import { DMORegisteredProfile } from '@/types/dmo-management.types';
import { ServiceProviderResponse } from '@/types/service-provider.types';

interface EntitlementGrantFormProps {
  readOnly?: boolean;
}

const ACTOR_TYPE_OPTIONS = Object.values(EntitlementActorType);

const actorTypeToSubscriptionType: Record<
  EntitlementActorType,
  SubscriptionType
> = {
  [EntitlementActorType.BOATER]: SubscriptionType.BOATER,
  [EntitlementActorType.HARBOR]: SubscriptionType.HARBOR,
  [EntitlementActorType.CLUB]: SubscriptionType.CLUB,
  [EntitlementActorType.DMO]: SubscriptionType.DMO,
  [EntitlementActorType.SERVICE_PROVIDER]: SubscriptionType.SERVICE_PROVIDER,
};

const EntitlementGrantForm = ({
  readOnly = false,
}: EntitlementGrantFormProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [createGrant, { isLoading }] = useCreateEntitlementGrantMutation();

  const [actorType, setActorType] = useState<EntitlementActorType>(
    EntitlementActorType.BOATER
  );
  const [actorId, setActorId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data: usersData, isFetching: isUsersFetching } = useGetUsersQuery(
    { user_type: UserTypes.BOATER, limit: 100, search: debouncedSearchTerm },
    { skip: actorType !== EntitlementActorType.BOATER }
  );

  const { data: harborsData, isFetching: isHarborsFetching } =
    useGetHarborsQuery(
      { limit: 100, search: debouncedSearchTerm },
      { skip: actorType !== EntitlementActorType.HARBOR }
    );

  const { data: yachtsData, isFetching: isYachtsFetching } = useGetYachtsQuery(
    { limit: 100, search: debouncedSearchTerm },
    { skip: actorType !== EntitlementActorType.CLUB }
  );

  const { data: dmosData, isFetching: isDmosFetching } =
    useGetRegisteredDMOsQuery(
      { limit: 100, search: debouncedSearchTerm },
      { skip: actorType !== EntitlementActorType.DMO }
    );

  const { data: serviceProvidersData, isFetching: isServiceProvidersFetching } =
    useGetServiceProvidersQuery(
      { limit: 100, search: debouncedSearchTerm },
      { skip: actorType !== EntitlementActorType.SERVICE_PROVIDER }
    );

  const actorOptions: { value: string; label: string }[] = useMemo(() => {
    switch (actorType) {
      case EntitlementActorType.BOATER:
        return ((usersData?.data as unknown as UserResponse[]) || []).map(
          (user: UserResponse) => ({
            value: user.id || '',
            label: user.name || user.email || '',
          })
        );
      case EntitlementActorType.HARBOR:
        return (
          harborsData?.data?.map((harbor: HarborResponse) => ({
            value: harbor.id || '',
            label: harbor.name || '',
          })) || []
        );
      case EntitlementActorType.CLUB:
        return (
          yachtsData?.data?.map((club: IYachtData) => ({
            value: club.id || '',
            label: club.name || '',
          })) || []
        );
      case EntitlementActorType.DMO:
        return (
          dmosData?.data?.map((dmo: DMORegisteredProfile) => ({
            value: dmo.id || '',
            label: dmo.organisation_name || '',
          })) || []
        );
      case EntitlementActorType.SERVICE_PROVIDER:
        return (
          serviceProvidersData?.data?.map((sp: ServiceProviderResponse) => ({
            value: sp.id || '',
            label: sp.display_name || sp.legal_name || '',
          })) || []
        );
      default:
        return [];
    }
  }, [
    actorType,
    usersData,
    harborsData,
    yachtsData,
    dmosData,
    serviceProvidersData,
  ]);

  const isActorFetching = useMemo(() => {
    switch (actorType) {
      case EntitlementActorType.BOATER:
        return isUsersFetching;
      case EntitlementActorType.HARBOR:
        return isHarborsFetching;
      case EntitlementActorType.CLUB:
        return isYachtsFetching;
      case EntitlementActorType.DMO:
        return isDmosFetching;
      case EntitlementActorType.SERVICE_PROVIDER:
        return isServiceProvidersFetching;
      default:
        return false;
    }
  }, [
    actorType,
    isUsersFetching,
    isHarborsFetching,
    isYachtsFetching,
    isDmosFetching,
    isServiceProvidersFetching,
  ]);

  const handleActorTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setActorType(e.target.value as EntitlementActorType);
    setActorId('');
    setSearchTerm('');
  };
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [referenceSubscriptionId, setReferenceSubscriptionId] = useState('');
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Convert HTML string to plain text by stripping tags and decoding entities
  const toPlainText = (html: unknown): string => {
    if (typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  };

  useEffect(() => {
    const loadSubscriptions = async () => {
      try {
        const response = await SubscriptionsService.getSubscriptions(
          1,
          100,
          '',
          undefined,
          actorTypeToSubscriptionType[actorType]
        );
        const rows = response.subscriptions ?? [];
        const options = rows.map((item) => ({
          id: item.id,
          name: toPlainText(item.name) || item.id,
        }));
        setSubscriptionOptions(options);
        setReferenceSubscriptionId(options[0]?.id ?? '');
      } catch {
        setSubscriptionOptions([]);
        setReferenceSubscriptionId('');
      }
    };

    void loadSubscriptions();
  }, [actorType]);

  const canSubmit = useMemo(
    () =>
      !!actorId.trim() &&
      !!startAt &&
      !!endAt &&
      !!referenceSubscriptionId &&
      !readOnly,
    [actorId, startAt, endAt, referenceSubscriptionId, readOnly]
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      await createGrant({
        actor_type: actorType,
        actor_id: actorId.trim(),
        grant_type: EntitlementGrantType.MANUAL,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        reference_subscription_id: referenceSubscriptionId,
      }).unwrap();

      enqueueSnackbar('Entitlement grant created', { variant: 'success' });
      navigate('/configuration/entitlement-grants');
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to create entitlement grant';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <h3 className="text-lg font-semibold">
        Create Manual Entitlement Grant
      </h3>

      <div className="grid gap-2">
        <label htmlFor="grant-actor-type">Actor Type</label>
        <select
          id="grant-actor-type"
          className="select select-bordered"
          value={actorType}
          disabled={readOnly}
          onChange={handleActorTypeChange}
        >
          {ACTOR_TYPE_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-2" style={{ zIndex: 10 }}>
        <label htmlFor="grant-actor-id">Actor ID</label>
        <Select
          id="grant-actor-id"
          options={actorOptions}
          value={
            actorOptions.find((option) => option.value === actorId) || null
          }
          onChange={(selected) => setActorId(selected ? selected.value : '')}
          onInputChange={(value, actionMeta) => {
            if (actionMeta.action === 'input-change') {
              setSearchTerm(value);
            }
          }}
          isDisabled={readOnly}
          isLoading={isActorFetching}
          placeholder="Search Actor..."
          classNamePrefix="react-select"
          isClearable
        />
      </div>

      <div className="grid gap-2">
        <label htmlFor="grant-subscription">Subscription</label>
        <select
          id="grant-subscription"
          className="select select-bordered"
          value={referenceSubscriptionId}
          disabled={readOnly}
          onChange={(e) => setReferenceSubscriptionId(e.target.value)}
        >
          {subscriptionOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-2">
        <label htmlFor="grant-start-at">Start At</label>
        <input
          id="grant-start-at"
          type="datetime-local"
          className="input input-bordered"
          value={startAt}
          disabled={readOnly}
          min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16)}
          onChange={(e) => setStartAt(e.target.value)}
        />
      </div>

      <div className="grid gap-2">
        <label htmlFor="grant-end-at">End At</label>
        <input
          id="grant-end-at"
          type="datetime-local"
          className="input input-bordered"
          value={endAt}
          disabled={readOnly}
          min={
            startAt ||
            new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
              .toISOString()
              .slice(0, 16)
          }
          onChange={(e) => setEndAt(e.target.value)}
        />
      </div>

      <div className="flex gap-2 pt-4 border-t">
        <button
          type="button"
          className="btn btn-light"
          onClick={() => navigate('/configuration/entitlement-grants')}
        >
          Cancel
        </button>
        {!readOnly && (
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Grant'}
          </button>
        )}
      </div>
    </form>
  );
};

export { EntitlementGrantForm };
